import React from 'react';
import { BankAccount, TaxTreatment, formatEuro, formatIban, payableTaxLabel } from '@radevents/shared';
import { BERRY, BORDER, FONT_HEAD, FONT_MONO, INK, MUTED } from './organizerSpace';

/**
 * Die Bestätigung nach einer Bestellung — eine Ansicht für alle Buchungsarten.
 *
 * Premium und Hervorhebung laufen über denselben Weg (Anfrage, Vorkasse, Freischaltung), also
 * gehören sie an dieselbe Ansicht: zwei Nachbauten desselben Dankes liefen auseinander, sobald
 * einer gepflegt wird — und beim Geld ist das die teuerste Stelle dafür. Die Hervorhebung hatte
 * bis zum 02.09.2026 gar keine, sondern nur einen Toast; nach dem Klick auf „Hervorheben"
 * schloss sich das Fenster, und was als Nächstes passiert, stand nirgends.
 */
export const BOOKING_DONE_CSS = `
/* Mittig: eine Bestätigung ist ein Ergebnis, keine Seite zum Weiterlesen. */
.bd { position:relative; padding:34px clamp(22px,4vw,34px) 30px; text-align:center; }
.bd-mark { display:block; margin:0 auto 20px; }
.bd h3 { font-family:${FONT_HEAD}; font-weight:800; font-size:26px; letter-spacing:-.015em;
  color:${INK}; margin:0; line-height:1.2; }
.bd p { font-size:14.5px; color:#6A6A6A; line-height:1.6; margin:12px auto 0; max-width:46ch; }
/* Verwendungszweck und IBAN werden abgetippt — Monoschrift, damit 0 und O verschieden aussehen,
   und auswaehlbar zum Kopieren. Als eigene Flaeche, weil zwei Spalten in einer mittigen
   Bestaetigung sonst als lose Zeilenpaare zerfielen; innen bleibt sie linksbuendig, denn zwei
   Spalten liest man an einer Kante entlang. */
.bd-pay { border-collapse:collapse; margin:22px auto 0; text-align:left;
  background:#F8F6F6; border-radius:14px; padding:14px 20px; }
.bd-pay th { text-align:left; font-weight:400; font-size:13.5px; color:${MUTED};
  padding:4px 18px 4px 0; white-space:nowrap; vertical-align:baseline; }
.bd-pay td { font-size:15px; font-weight:700; color:${INK}; padding:4px 0; }
.bd-pay-mono { font-family:${FONT_MONO}; user-select:all; }
.bd-pay-vat { font-weight:400; font-size:13px; color:${MUTED}; }
/* Der Knopf steht mittig unter der Bestaetigung, nicht rechts aussen: dort ist er der naechste
   Schritt, hier waere er ein „weiter" in einem Ablauf, der zu Ende ist. */
.bd-foot { display:flex; justify-content:center; padding:22px 0 0; }
.bd-cta { font-family:inherit; font-size:14px; font-weight:700; border-radius:10px;
  padding:12px 20px; cursor:pointer; border:1px solid ${BERRY}; background:${BERRY}; color:#fff; }
.bd-cta:hover { background:#7E2549; border-color:#7E2549; }
.bd-cta:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }
.bd-close { position:absolute; top:16px; right:16px; width:34px; height:34px; border-radius:50%;
  display:flex; align-items:center; justify-content:center; cursor:pointer;
  border:1px solid ${BORDER}; background:#fff; color:${INK}; }
.bd-close:hover { background:#F5F3F3; }
.bd-close:focus-visible { outline:2px solid ${BERRY}; outline-offset:2px; }
`;

/**
 * Das Zeichen über der Bestätigung: eine auf die Ecke gestellte Marken-Raute mit Haken.
 *
 * Als **eine** SVG-Fläche gezeichnet und nicht als per CSS gedrehtes `div`: ein gedrehtes Quadrat
 * behält seinen alten Kasten und ragt oben und unten darüber hinaus — der Abstand zur Überschrift
 * stimmte dann je nach Größe nicht mehr. Der Haken trägt kein `aria`, weil die Überschrift
 * daneben dasselbe in Worten sagt.
 */
const DoneMark: React.FC = () => (
  <svg className="bd-mark" viewBox="0 0 96 96" width="88" height="88" aria-hidden>
    <rect x="18" y="18" width="60" height="60" rx="17" transform="rotate(45 48 48)" fill={BERRY} />
    <path d="M34 48.5 L44 58.5 L63 37" fill="none" stroke="#fff" strokeWidth="6.5"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface Props {
  /**
   * Was nach dem Geldeingang passiert, als Satzende: „schalten wir Premium frei".
   *
   * Der Rest des Satzes steht hier, damit alle Bestätigungen gleich klingen — es ist derselbe
   * Ablauf, nur mit einer anderen Leistung am Ende.
   */
  next: string;
  /**
   * Der zu überweisende Bruttobetrag.
   *
   * Angeboten wird netto (V159), überwiesen brutto — der Kunde hat also gerade eine kleinere Zahl
   * gesehen. Deshalb steht die Steuer hier dabei, statt dass die Aufstellung wortlos mehr
   * verlangt, als im Dialog stand.
   */
  grossCents: number;
  vatPercent: number;
  /** Steuerfall der Bestellung — ohne MwSt. steht dort der Grund statt „inkl. 0 % MwSt.". */
  taxTreatment?: TaxTreatment;
  /** Verwendungszweck der Überweisung. */
  reference: string;
  /**
   * Wohin das Geld soll. Fehlt, wenn keine `BILLING_IBAN` hinterlegt ist — dann entfällt die
   * Aufstellung ganz und der Satz nennt die Mail als Quelle. Ein Verweis auf eine Aufstellung,
   * die darunter fehlt, ist schlimmer als kein Verweis.
   */
  bank?: BankAccount;
  onClose: () => void;
}

const BookingDone: React.FC<Props> = ({ next, grossCents, vatPercent, taxTreatment, reference, bank, onClose }) => (
  <div className="bd">
    <button type="button" className="bd-close" onClick={onClose} aria-label="Schließen">
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
    <DoneMark />
    <h3>Vielen Dank für deine Buchung</h3>
    {/* Was als Nächstes passiert, in der Reihenfolge, in der es passiert: Mail, Zahlung,
        Freischaltung. */}
    <p>
      Du erhältst eine Bestätigung {bank ? 'mit allen Angaben' : 'und die Zahlungsdaten'} per
      E-Mail. Sobald deine Zahlung eingegangen ist, {next}.
    </p>
    {/* Zeigen statt beschreiben: der Satz „wir haben dir eine Mail mit der Bankverbindung
        geschickt" ist länger als die Bankverbindung selbst — und wer gerade überweisen will,
        müsste dafür das Fenster verlassen. Der Betrag steht als erste Zeile: er ist die Angabe,
        die beim Überweisen zuerst gebraucht wird, und die Überschrift trägt ihn nicht mehr. */}
    {bank && (
      <table className="bd-pay">
        <tbody>
          <tr>
            <th>Betrag</th>
            <td>{formatEuro(grossCents)} <span className="bd-pay-vat">{payableTaxLabel(taxTreatment, vatPercent)}</span></td>
          </tr>
          <tr><th>Verwendungszweck</th><td className="bd-pay-mono">{reference}</td></tr>
          <tr><th>Empfänger</th><td>{bank.accountHolder}</td></tr>
          {/* Banken im Ausland verlangen die Anschrift des Empfängers (AUDAX Suisse, 24.10.2026). */}
          {bank.accountHolderAddress && <tr><th>Anschrift</th><td>{bank.accountHolderAddress}</td></tr>}
          <tr><th>IBAN</th><td className="bd-pay-mono">{formatIban(bank.iban)}</td></tr>
          {bank.bic && <tr><th>BIC</th><td className="bd-pay-mono">{bank.bic}</td></tr>}
        </tbody>
      </table>
    )}
    <div className="bd-foot">
      <button type="button" className="bd-cta" onClick={onClose}>Schließen</button>
    </div>
  </div>
);

export default BookingDone;
